import { Button, Caption, Flex, Icons, View } from '@tabeo/scarf'
import ACL from 'components/ACL'
import useACL from 'hooks/useACL'
import { Link } from 'react-router'
import { useAuth } from 'resources/Auth'
import NavItem from './NavItem'
import NestedNav from './NestedNav'
import CogsIcon from './assets/cogs.svg?react'
import AccountIcon from './assets/navigation-menu-account.svg?react'
import BillsIcon from './assets/navigation-menu-bills.svg?react'
import BlockedPayoutsIcon from './assets/navigation-menu-blocked-payouts.svg?react'
import BorrowersIcon from './assets/navigation-menu-borrowers.svg?react'
import FinanceLedgerIcon from './assets/navigation-menu-finance-ledger.svg?react'
import FinancePricingIcon from './assets/navigation-menu-finance-pricing.svg?react'
import InstructionsIcon from './assets/navigation-menu-instructions.svg?react'
import LeadsIcon from './assets/navigation-menu-leads.svg?react'
import LinkPaymentsIcon from './assets/navigation-menu-links.svg?react'
import MerchantsIcon from './assets/navigation-menu-merchants.svg?react'
import PayoutsIcon from './assets/navigation-menu-payouts.svg?react'
import RefundsIcon from './assets/navigation-menu-refunds.svg?react'
import SalesToolsIcon from './assets/navigation-menu-sales-tools.svg?react'
import SignOutIcon from './assets/navigation-menu-sign-out.svg?react'
import SubscriptionsIcon from './assets/navigation-menu-subscriptions.svg?react'
import TerminalPaymentsIcon from './assets/navigation-menu-terminal.svg?react'
import TicketsIcon from './assets/navigation-menu-tickets.svg?react'
import TransactionsIcon from './assets/navigation-menu-transactions.svg?react'
import tabeoIcon from './assets/tabeo-icon.svg'

function Sidebar(props) {
  const {
    data: { user },
    resource: { logout },
  } = useAuth()

  const isCollectionSpecialist = useACL(['collections_specialist'])

  return (
    <Flex
      flexDirection="column"
      alignItems="stretch"
      bg="white"
      boxShadow={2}
      flexShrink="0"
      borderRight={1}
      data-testid="sidebar"
      {...props}
    >
      <View flex="1">
        <View p={4}>
          <Flex mb={4} display={['none', 'flex']}>
            <View as="img" src={tabeoIcon} mr={2} flexShrink="0" />
            <Caption color="ink.0" fontWeight="semibold" flex="1">
              Tabeo Admin Panel
            </Caption>
          </Flex>
          <ACL deny={['auditor']}>
            <Button
              leftIcon={p => <Icons.Plus light {...p} />}
              width="100%"
              as={Link}
              to="?ticketId=new"
              replace
            >
              New ticket
            </Button>
          </ACL>
        </View>
        <View display="grid" gridGap={1} gridTemplateColumns="1fr">
          <ACL deny={['auditor']}>
            <NavItem to="/tickets" icon={TicketsIcon}>
              Tickets
            </NavItem>
          </ACL>
          <ACL
            allow={[
              'operations_specialist',
              'support_specialist',
              'growth_specialist',
              'collections_specialist',
              'developer',
              'auditor',
            ]}
          >
            <NavItem to="/transactions" icon={TransactionsIcon}>
              Transactions
            </NavItem>
          </ACL>
          <ACL
            allow={[
              'operations_specialist',
              'support_specialist',
              'growth_specialist',
              'developer',
            ]}
          >
            <NavItem to="/merchants" icon={MerchantsIcon}>
              Merchants
            </NavItem>
          </ACL>
          <ACL allow={['collections_specialist', 'developer', 'auditor']}>
            <NestedNav
              to="/arrears-cases"
              title="Collections"
              icon={BorrowersIcon}
            >
              {isCollectionSpecialist && (
                <NavItem to="/arrears-cases/assigned-to-me" icon={View}>
                  Assigned to me
                </NavItem>
              )}
            </NestedNav>
          </ACL>
          <ACL
            allow={[
              'operations_specialist',
              'support_specialist',
              'growth_specialist',
              'developer',
            ]}
          >
            <NavItem to="/leads" icon={LeadsIcon}>
              Leads
            </NavItem>
          </ACL>
          <ACL
            allow={[
              'operations_specialist',
              'support_specialist',
              'growth_specialist',
              'developer',
            ]}
          >
            <NavItem to="/bills" icon={BillsIcon}>
              Bills
            </NavItem>
          </ACL>
          <ACL allow={['developer']}>
            <NavItem to="/instructions" icon={InstructionsIcon}>
              Instructions
            </NavItem>
            <NavItem to="/payouts" icon={PayoutsIcon}>
              Payouts
            </NavItem>
            <NavItem to="/merchant-payouts" icon={PayoutsIcon}>
              Merchant payouts
            </NavItem>
            <NavItem to="/blocked-payouts" icon={BlockedPayoutsIcon}>
              Blocked payouts
            </NavItem>
          </ACL>
          <ACL allow={['finance_write', 'finance_approve']}>
            <NavItem to="/finance-ledger" icon={FinanceLedgerIcon}>
              Finance ledger
            </NavItem>
          </ACL>
          <ACL allow={['operations_specialist']}>
            <NavItem to="/finance-pricing" icon={FinancePricingIcon}>
              Finance pricing
            </NavItem>
          </ACL>
          <ACL
            allow={[
              'operations_specialist',
              'support_specialist',
              'growth_specialist',
              'developer',
            ]}
          >
            <NavItem to="/subscriptions" icon={SubscriptionsIcon}>
              Subscriptions
            </NavItem>
          </ACL>
          <ACL
            allow={[
              'operations_specialist',
              'support_specialist',
              'growth_specialist',
              'developer',
            ]}
          >
            <NestedNav icon={SubscriptionsIcon} title="Plans" alwaysOpen>
              <NavItem
                to="/plans-v2/members"
                activePaths={['/subscriptions-v2', '/new-subscription-v2']}
                icon={View}
              >
                Members
              </NavItem>
            </NestedNav>
          </ACL>
          <ACL
            allow={[
              'operations_specialist',
              'support_specialist',
              'growth_specialist',
              'collections_specialist',
              'developer',
              'auditor',
            ]}
          >
            <NavItem to="/terminal-payments" icon={TerminalPaymentsIcon}>
              Terminal Payments
            </NavItem>
          </ACL>
          <ACL
            allow={[
              'operations_specialist',
              'support_specialist',
              'growth_specialist',
              'collections_specialist',
              'developer',
              'auditor',
            ]}
          >
            <NavItem to="/link-payments" icon={LinkPaymentsIcon}>
              Payment Links
            </NavItem>
          </ACL>
          <ACL
            allow={[
              'operations_specialist',
              'support_specialist',
              'growth_specialist',
              'collections_specialist',
              'developer',
              'auditor',
            ]}
          >
            <NavItem to="/refunds" icon={RefundsIcon}>
              Refunds
            </NavItem>
          </ACL>
          <ACL
            allow={[
              'operations_specialist',
              'support_specialist',
              'growth_specialist',
              'developer',
            ]}
          >
            <NestedNav icon={SalesToolsIcon} title="Sales tools" alwaysOpen>
              <NavItem to="/sales-tools/plans" icon={View}>
                Plans
              </NavItem>
              <NavItem to="/sales-tools/finance" icon={View}>
                Finance
              </NavItem>
              {/* A static page served by this app (public/tools/), not a
                  route: a plain link so the browser leaves the SPA. */}
              <NavItem href="/tools/nhs-uda-lookup.html" icon={View}>
                NHS
              </NavItem>
              <NavItem href="/tools/plan-member-finder.html" icon={View}>
                Member finder
              </NavItem>
            </NestedNav>
          </ACL>
        </View>
      </View>
      <View flexShrink="0" borderTop={1} py={4}>
        <ACL
          allow={[
            'developer',
            'operations_specialist',
            'support_specialist',
            'growth_specialist',
          ]}
        >
          <NavItem to="/settings" icon={CogsIcon}>
            Settings
          </NavItem>
        </ACL>
        {user && (
          <NavItem to="/account" icon={AccountIcon}>
            {user.firstName} {user.lastName}
          </NavItem>
        )}
        <NavItem icon={SignOutIcon} onClick={logout}>
          Sign out
        </NavItem>
      </View>
    </Flex>
  )
}

Sidebar.propTypes = {}

export default Sidebar
